import styles from "./ServiceArt.module.css";

/**
 * Decorative artwork panel for featured services.
 *
 * If `src` is given it renders that photograph. Otherwise it draws a
 * generated SVG scene, so a featured block still looks composed before
 * real clinic photography exists — rather than a grey placeholder box
 * or stock imagery pretending to be this practice.
 *
 * To use a real photo: drop it in src/Photos/ and pass it as `src`.
 */

const scenes = {
  /* Concentric rings + suction cups — reads as cupping without
     attempting anatomical illustration. */
  cupping: (
    <>
      <defs>
        <radialGradient id="art-cup-glow" cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="var(--a-ember)" stopOpacity="0.4" />
          <stop offset="70%" stopColor="var(--a-ember)" stopOpacity="0.05" />
          <stop offset="100%" stopColor="var(--a-ember)" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="400" height="300" fill="url(#art-cup-glow)" />

      {/* fascia lines */}
      <g stroke="currentColor" strokeOpacity="0.28" fill="none" strokeWidth="1.2">
        <path d="M-10 210c60-26 120-26 180 0s120 26 180 0" />
        <path d="M-10 232c60-26 120-26 180 0s120 26 180 0" />
        <path d="M-10 254c60-26 120-26 180 0s120 26 180 0" />
        <path d="M-10 276c60-26 120-26 180 0s120 26 180 0" />
      </g>

      {/* three cups, largest centred */}
      <g fill="none" strokeWidth="2.2" stroke="currentColor">
        <g opacity="0.9">
          <ellipse cx="200" cy="120" rx="46" ry="16" />
          <path d="M154 120v34a46 26 0 0 0 92 0v-34" />
          <ellipse cx="200" cy="154" rx="46" ry="20" strokeOpacity="0.4" />
        </g>
        <g opacity="0.6">
          <ellipse cx="104" cy="140" rx="32" ry="11" />
          <path d="M72 140v24a32 18 0 0 0 64 0v-24" />
        </g>
        <g opacity="0.6">
          <ellipse cx="296" cy="140" rx="32" ry="11" />
          <path d="M264 140v24a32 18 0 0 0 64 0v-24" />
        </g>
      </g>

      {/* gua sha stroke marks */}
      <g stroke="currentColor" strokeOpacity="0.35" strokeWidth="3" strokeLinecap="round">
        <path d="M120 62h58" />
        <path d="M140 46h58" />
        <path d="M222 62h58" />
        <path d="M202 46h58" />
      </g>
    </>
  ),

  /* Ascending bars + motion arc — progressive loading. */
  progression: (
    <>
      <defs>
        <linearGradient id="art-prog" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--a-teal)" stopOpacity="0.5" />
          <stop offset="100%" stopColor="var(--a-marine)" stopOpacity="0.15" />
        </linearGradient>
      </defs>

      <g fill="url(#art-prog)">
        <rect x="42" y="200" width="46" height="70" rx="8" />
        <rect x="106" y="168" width="46" height="102" rx="8" />
        <rect x="170" y="130" width="46" height="140" rx="8" />
        <rect x="234" y="86" width="46" height="184" rx="8" />
        <rect x="298" y="40" width="46" height="230" rx="8" />
      </g>

      <path
        d="M52 190c70-30 150-84 282-150"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeDasharray="7 9"
        opacity="0.55"
      />

      <g fill="currentColor" opacity="0.75">
        <circle cx="65" cy="196" r="5" />
        <circle cx="193" cy="126" r="5" />
        <circle cx="321" cy="42" r="5" />
      </g>
    </>
  ),
};

export default function ServiceArt({ art = "progression", src, alt = "", tone = "light" }) {
  if (src) {
    return (
      <figure className={`${styles.frame} ${styles[tone]}`}>
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      </figure>
    );
  }

  return (
    <div className={`${styles.frame} ${styles[tone]}`} aria-hidden="true">
      <svg
        viewBox="0 0 400 300"
        preserveAspectRatio="xMidYMid slice"
        className={styles.svg}
        focusable="false"
      >
        {scenes[art] || scenes.progression}
      </svg>
    </div>
  );
}
